import type {
	Quiz,
	QuizPopulated,
	QuizQuestion,
	QuizQuestionRepetition,
	QuizRepetition,
} from "@vita-learning/types";
import { isValid } from "date-fns";
import isUrl from "is-url";

import { decodeHtml, EnhancedError, isHexId, shuffle } from "~/shared/utils";

import { QUIZ_SOURCES } from "./constants";
import type { QuizInfoForSource } from "./types";

export enum QuizErrorId {
	TITLE = "QUIZ_TITLE",
	DATE = "QUIZ_DATE",
	TARGET_SCORE = "QUIZ_TARGET_SCORE",
	SOURCE = "QUIZ_SOURCE",
	QUESTIONS = "QUIZ_QUESTIONS",
	FILE = "QUIZ_FILE",
	NOTE = "QUIZ_NOTE",
	FORMAT = "QUIZ_FORMAT",
	URL = "QUIZ_URL",
}

export function assertsValidPartialQuiz(
	quiz: unknown,
	file?: File
): asserts quiz is QuizInfoForSource {
	if (!quiz || typeof quiz !== "object")
		throw new Error("Quiz must be an object");

	if (!("userId" in quiz) || !isHexId(quiz.userId))
		throw new Error("Quiz requires a valid user ID");

	if (!("title" in quiz) || !quiz.title || typeof quiz.title !== "string")
		throw new EnhancedError("Title is a required field", QuizErrorId.TITLE);

	if (
		"date" in quiz &&
		(typeof quiz.date !== "string" || !isValid(new Date(quiz.date)))
	)
		throw new EnhancedError(
			"Date string is malformed. A valid date string is required",
			QuizErrorId.DATE
		);

	if (
		!("targetScore" in quiz) ||
		typeof quiz.targetScore !== "number" ||
		quiz.targetScore < 0 ||
		quiz.targetScore > 100
	)
		throw new EnhancedError(
			"Target score must be a number between 0 and 100",
			QuizErrorId.TARGET_SCORE
		);

	if (!("source" in quiz) || !isQuizSource(quiz.source))
		throw new EnhancedError("Invalid source", QuizErrorId.SOURCE);

	switch (quiz.source) {
		case "file":
			if (!file)
				throw new EnhancedError(
					"A file is a required when Source is 'File'",
					QuizErrorId.FILE
				);
			break;
		case "note":
			if (!("noteId" in quiz))
				throw new EnhancedError(
					"A Note is a required when Source is 'Note'",
					QuizErrorId.NOTE
				);

			if (typeof quiz.noteId !== "string" || !isHexId(quiz.noteId))
				throw new EnhancedError(
					"Note ID is malformed. A valid ID is required",
					QuizErrorId.NOTE
				);
			break;
		case "url":
			if (!("url" in quiz))
				throw new EnhancedError(
					"A URL is a required when Source is 'URL'",
					QuizErrorId.URL
				);

			if (typeof quiz.url !== "string" || !isUrl(quiz.url))
				throw new EnhancedError("URL must be a valid URL", QuizErrorId.URL);
			break;
	}
}

export const isValidPartialQuiz = (
	quiz: unknown,
	file?: File
): quiz is QuizInfoForSource => {
	try {
		assertsValidPartialQuiz(quiz, file);
		return true;
	} catch {
		return false;
	}
};

function assertsValidQuestion(
	question: unknown,
	index: number
): asserts question is QuizQuestion {
	const errorId = `${QuizErrorId.QUESTIONS}_${index}`;

	if (!question || typeof question !== "object")
		throw new EnhancedError("Question is malformed", errorId);

	if (!("id" in question) || !isHexId(question.id))
		throw new EnhancedError("Question requires a valid unique ID", errorId);

	if (
		!("prompt" in question) ||
		!question.prompt ||
		typeof question.prompt !== "string" ||
		!question.prompt.trim()
	)
		throw new EnhancedError("Prompt must be a non-empty string", errorId);

	if (
		!("type" in question) ||
		typeof question.type !== "string" ||
		!question.type.trim()
	)
		throw new EnhancedError("Invalid question type", errorId);

	switch (question.type) {
		case "m/c": {
			if (!("answer" in question) || typeof question.answer !== "number")
				throw new EnhancedError("Answer must be a number", errorId);

			if (
				!("originalAnswer" in question) ||
				typeof question.originalAnswer !== "number"
			)
				throw new EnhancedError("Original answer must be a number", errorId);

			if (question.answer !== question.originalAnswer)
				throw new EnhancedError(
					"Answer and original answer must be the same",
					errorId
				);

			if (
				!("options" in question) ||
				!Array.isArray(question.options) ||
				question.options.length < 4
			)
				throw new EnhancedError(
					"Question must have at least 4 options",
					errorId
				);

			if (
				!question.options.every(
					o => typeof o === "string" && o.trim().length > 0
				)
			)
				throw new EnhancedError("Options must be non-empty strings", errorId);

			break;
		}
		default:
			break;
	}
}

export function assertsValidQuiz(
	quiz: unknown,
	file?: File
): asserts quiz is Quiz {
	assertsValidPartialQuiz(quiz, file);

	if (!("id" in quiz) || !isHexId(quiz.id))
		throw new Error(
			"Generated quiz set must have a valid ID in order to submit. Try again"
		);

	if (!("questions" in quiz) || !Array.isArray(quiz.questions))
		throw new EnhancedError(
			"Generated quiz contains malformed questions. Try again",
			QuizErrorId.QUESTIONS
		);

	if (quiz.questions.length < 3)
		throw new EnhancedError(
			"Generated quiz must contain at least three questions. Try again",
			QuizErrorId.QUESTIONS
		);

	quiz.questions.forEach(assertsValidQuestion);

	if (
		!("repetitions" in quiz) ||
		!Array.isArray(quiz.repetitions) ||
		quiz.repetitions.length !== 0
	)
		throw new Error("Quiz repetitions are malformed");

	if (!("isCompleted" in quiz) || quiz.isCompleted !== false)
		throw new Error("Completion flag must be false when creating a new quiz");

	if (!("meta" in quiz) || !quiz.meta || typeof quiz.meta !== "object")
		throw new Error("Generated quiz contains malformed metadata. Try again");

	if (
		!("createdAt" in quiz.meta) ||
		!("updatedAt" in quiz.meta) ||
		!("version" in quiz.meta)
	)
		throw new Error("Generated quiz contains malformed metadata. Try again");

	if (
		typeof quiz.meta.createdAt !== "string" ||
		typeof quiz.meta.updatedAt !== "string" ||
		typeof quiz.meta.version !== "number"
	)
		throw new Error("Generated quiz contains malformed metadata. Try again");
}

export const isValidQuiz = (quiz: unknown, file?: File): quiz is Quiz => {
	try {
		assertsValidQuiz(quiz, file);
		return true;
	} catch {
		return false;
	}
};

/**
 * Calculate the Levenshtein distance between two strings.
 * The Levenshtein distance is a measure of the similarity between two strings
 * by counting the minimum number of single-character edits (insertions, deletions,
 * or substitutions) required to change one string into the other.
 * @param {string} a
 * @param {string} b
 * @returns {number} - The Levenshtein distance between the two strings.
 */
function levenshteinDistance(a: string, b: string): number {
	const aLen = a.length;
	const bLen = b.length;

	if (aLen === 0) return bLen;
	if (bLen === 0) return aLen;

	const dp: number[][] = Array.from({ length: aLen + 1 }, () =>
		new Array(bLen + 1).fill(0)
	);

	for (let i = 0; i <= aLen; i++) dp[i]![0] = i;
	for (let j = 0; j <= bLen; j++) dp[0]![j] = j;

	for (let i = 1; i <= aLen; i++) {
		for (let j = 1; j <= bLen; j++) {
			const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;

			dp[i]![j] = Math.min(
				(dp[i - 1]![j] as number) + 1, // deletion
				(dp[i]![j - 1] as number) + 1, // insertion
				(dp[i - 1]![j - 1] as number) + cost // substitution
			);
		}
	}

	return dp[aLen]![bLen] as number;
}

/**
 * Check if the student's response to a short answer question is correct.
 * This function normalizes the expected answer and the student's response
 * by removing punctuation, converting to lowercase, and collapsing multiple spaces.
 * It then checks for an exact match or a high overlap ratio of words.
 * If the expected answer and the student's response are both short phrases,
 * it also checks the Levenshtein distance to determine if they are similar.
 * @param {string} answer
 * @param {string} response
 * @returns {boolean} - True if the answer is correct, false otherwise.
 */
export function isShortAnswerCorrect(
	answer: string,
	response: string
): boolean {
	const normalize = (str: string): string =>
		str
			.toLowerCase()
			.replace(/[^\w\s]/gi, "") // remove punctuation
			.replace(/\s+/g, " ") // collapse multiple spaces
			.trim();

	const normalizedAnswer = normalize(answer);
	const normalizedResponse = normalize(response);

	// Exact match after normalization
	if (normalizedAnswer === normalizedResponse) return true;

	const answerTokens = new Set(normalizedAnswer.split(" "));
	const responseTokens = new Set(normalizedResponse.split(" "));

	const intersection = [...answerTokens].filter(word =>
		responseTokens.has(word)
	);
	const overlapRatio =
		answerTokens.size > 0 ? intersection.length / answerTokens.size : 0;

	if (overlapRatio >= 0.8) return true;

	// Levenshtein distance check for short phrases
	if (normalizedAnswer.length <= 20 && normalizedResponse.length <= 20) {
		const distance = levenshteinDistance(normalizedAnswer, normalizedResponse);
		const maxLength = Math.max(
			normalizedAnswer.length,
			normalizedResponse.length
		);
		const similarity = maxLength > 0 ? 1 - distance / maxLength : 0;
		return similarity >= 0.85;
	}

	return false;
}

/**
 *
 * @param {QuizRepetition} repetition quiz repetition
 * @returns {[number, number]} tuple containing correct count and question count
 */
export const getQuizRepetitionScore = (
	repetition: QuizRepetition
): [number, number] => {
	const questionCount = repetition.questions.length;
	const correctCount = repetition.questions.reduce((correct, question) => {
		function isCorrectAnswer(q: QuizQuestionRepetition) {
			switch (q.type) {
				case "m/c":
				case "t/f":
				case "numerical":
					return q.answer === q.response;
				case "m/a":
					if (!Array.isArray(q.answer)) return false;

					return q.answer.every((a, i) => a === q.response?.[i]);
				case "ordering":
				case "blank": {
					if (Array.isArray(q.answer)) {
						if (!Array.isArray(q.response)) return false;

						return q.answer.every(
							(a, i) =>
								typeof a === "string" &&
								typeof q.response?.[i] === "string" &&
								a.toLowerCase() === q.response[i].toLowerCase()
						);
					}

					if (typeof q.response === "string")
						return q.answer.toLowerCase() === q.response.toLowerCase();

					return false;
				}
				case "s/a": {
					if (typeof q.response !== "string") return false;

					return isShortAnswerCorrect(q.answer, q.response);
				}
				case "matching": {
					if (
						!Array.isArray(q.response) ||
						q.response.length !== q.answer.length
					)
						return false;

					return q.answer.every((a, i) => {
						const ans = a as [string, string];
						const res = q.response[i] as [string, string];

						return (
							ans[0].toLowerCase() === res[0].toLowerCase() &&
							ans[1].toLowerCase() === res[1].toLowerCase()
						);
					});
				}
			}
		}

		if (isCorrectAnswer(question)) correct += 1;

		return correct;
	}, 0);

	return [correctCount, questionCount];
};

/**
 *
 * @param {QuizRepetition} reptitions quiz repetitions
 * @returns {[number, number] | undefined}
 */
export const getBestQuizScore = (repetitions: QuizRepetition[] = []) => {
	const [bestRep] = repetitions
		.map(getQuizRepetitionScore)
		.sort((a, b) => b[0] - a[0]);

	return bestRep;
};

export const isQuizSource = (source: unknown): source is Quiz["source"] =>
	QUIZ_SOURCES.some(s => s === source);

export const shuffleQuizMCQOptions = (q: QuizQuestion) => {
	if (q.type !== "m/c") return q;

	const correctOption = q.options[q.answer];

	if (!correctOption) throw new Error("Correct option index is out of bounds");

	const shuffledOptions = shuffle(q.options);
	const shuffledAnswer = shuffledOptions.indexOf(correctOption);

	return {
		...q,
		options: shuffledOptions,
		answer: shuffledAnswer,
	};
};

/**
 *
 * @param {} a
 * @param {} b
 * @returns
 */
export const sortQuizzesByCompletion = <T extends Quiz | QuizPopulated>(
	a: T,
	b: T
) => {
	// Incomplete items come first
	if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;

	// Parse dates only if they are defined
	const dateA = a.date ? new Date(a.date).getTime() : NaN;
	const dateB = b.date ? new Date(b.date).getTime() : NaN;

	// If both dates are valid, sort by older dates first
	if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) return dateA - dateB;

	// If one date is undefined or invalid, place it last
	if (Number.isNaN(dateA)) return 1;
	if (Number.isNaN(dateB)) return -1;
	// If both dates are undefined, maintain relative order
	return 0;
};

export const decodeQuiz = ({
	title,
	questions,
	repetitions,
	...quiz
}: QuizPopulated): QuizPopulated => ({
	...quiz,
	title: decodeHtml(title),
	repetitions: repetitions,
	questions: questions.map(q => {
		switch (q.type) {
			case "blank": {
				if (q.multi)
					return {
						...q,
						answer: q.answer.map(decodeHtml),
						prompt: decodeHtml(q.prompt),
					};

				return {
					...q,
					answer: decodeHtml(q.answer),
					prompt: decodeHtml(q.prompt),
				};
			}
			case "m/a":
			case "m/c":
				return {
					...q,
					options: q.options.map(decodeHtml),
					prompt: decodeHtml(q.prompt),
				};
			case "matching":
				return {
					...q,
					answer: q.answer.map(a => [decodeHtml(a[0]), decodeHtml(a[1])]) as [
						string,
						string,
					][],
					prompt: decodeHtml(q.prompt),
				};
			case "ordering":
				return {
					...q,
					answer: q.answer.map(decodeHtml),
					prompt: decodeHtml(q.prompt),
				};
			case "s/a":
				return {
					...q,
					answer: decodeHtml(q.answer),
					prompt: decodeHtml(q.prompt),
				};
			case "numerical":
			case "t/f":
				return {
					...q,
					prompt: decodeHtml(q.prompt),
				};
			default:
				return q;
		}
	}),
});
